/**
 * How far a trail particle may be from the ship that laid it down.
 *
 * ## The contract, and why it is spatial
 *
 * `maxLengthHulls` is described by the schema as "How far the residue may
 * stretch behind the ship" and by the authoring guide as "até onde o rastro se
 * estica". Both are distances, and the renderer's own comment agrees: "the band
 * a trail draws is exactly how far the ship travels during one particle's life".
 *
 * ## Why a distance cannot be kept by a lifespan
 *
 * The version this rule was added to did keep it by a lifespan, capping each
 * particle at `maxLength * diameter / speed` **using the speed of the frame it
 * was born in**. That bounds the distance only while the speed does not rise
 * afterwards:
 *
 * ```
 * d(t) = INTEGRAL of v(s) ds, from birth to t
 * T    = L / v(birth)
 * d(T) <= L   only if   v(s) <= v(birth) for every s
 * ```
 *
 * Gate V26.4.1 followed one particle through exactly that: born at 85 world
 * units per second, capped at 979 ms for that speed, and the ship then
 * accelerated to 340-491. It was still alive 6.7 hull diameters behind a hull
 * whose contract is 1.6. Over 424 particles measured outside their contract,
 * **402 had been shortened by the cap** and broke the length anyway.
 *
 * So the cap is not the guarantee. This is.
 *
 * ## What it measures
 *
 * The euclidean distance from the OWNING hull's centre to the particle's centre.
 * See `SPATIAL-CONTRACT.md`: centre rather than visible edge, because the cap it
 * replaces bounded emission positions and never the drawn size; euclidean rather
 * than longitudinal, because an old particle has no current direction of travel
 * to be projected onto and because euclidean can only ever be the stricter of
 * the two.
 */
export interface TrailRangeQuery {
  /** Centre of the hull that OWNS this particle, in world units. */
  readonly hullX: number;
  readonly hullY: number;
  /** Centre of the particle, in world units. */
  readonly particleX: number;
  readonly particleY: number;
  /** One hull diameter in world units, including the visual scale. */
  readonly diameter: number;
  /** The skin's own contract. Null means the layer declares no length. */
  readonly maxLengthHulls: number | null;
}

/**
 * Whether this particle has left the band its skin allows.
 *
 * Silent - false - when the layer declares no `maxLengthHulls`, or when the
 * diameter is not a usable number: a rule with nothing to measure against must
 * not start killing residue.
 */
export function trailParticleOutOfRange(query: TrailRangeQuery): boolean {
  const { maxLengthHulls, diameter } = query;
  if (maxLengthHulls === null || !(maxLengthHulls > 0) || !(diameter > 0)) {
    return false;
  }
  const dx = query.particleX - query.hullX;
  const dy = query.particleY - query.hullY;
  const limit = maxLengthHulls * diameter;
  // Squared on both sides: this runs per live particle per frame, and a square
  // root here buys nothing a comparison needs.
  return dx * dx + dy * dy > limit * limit;
}
